import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as CsvIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { TimezoneService } from '../services/timezoneService';
import { exportAbsenceToCSV, exportAbsenceToExcel, exportAbsenceToPDF } from '../utils/exportUtils';
import moment from 'moment-timezone';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';
import { getApprovedAbsenceDisplayLabel, isManualDayOffAbsence } from '../constants/absenceReasons';

interface AbsenceReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  absenceType: string;
  status: string;
  page: number;
  limit: number;
}

interface AbsenceRecord {
  id: string;
  userId: string;
  absenceType: 'sick' | 'vacation' | 'personal' | 'other' | 'unexcused' | 'holiday' | 'bereavement' | 'maternity' | 'jury_duty' | 'no_show' | 'training' | 'suspension';
  startDate: string;
  endDate: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    employeeId: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    departmentId: string;
    timezone?: string;
  };
  approvedByUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const AbsenceReportPage: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<AbsenceReportFilters>({
    startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    userId: '',
    departmentId: '',
    absenceType: '',
    status: '',
    page: 1,
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'employee' | 'type' | 'start' | 'end' | 'created' | 'status'>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // newest first by default

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Fetch absence report data
  const { data: absenceData, isLoading, error, refetch } = useQuery({
    queryKey: ['absenceReport', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.timeEntries.getAbsenceReport(params.toString());
      return response.data;
    },
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.users.getAll(),
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch departments for filter dropdown
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiClient.departments.getAll(),
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  const handleFilterChange = (field: keyof AbsenceReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      userId: '',
      departmentId: '',
      absenceType: '',
      status: '',
      page: 1,
      limit: 50,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilters(prev => ({ ...prev, page: value }));
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const fetchAllAbsencesForExport = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '' && key !== 'page' && key !== 'limit') {
        params.append(key, value.toString());
      }
    });

    params.set('page', '1');
    params.set('limit', '1000');

    let allRecords: any[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      params.set('page', page.toString());
      const response = await apiClient.timeEntries.getAbsenceReport(params.toString());
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;

      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const handleExportCSV = async () => {
    try {
      const data = await fetchAllAbsencesForExport();
      exportAbsenceToCSV(data, filters);
    } catch (error) {
      console.error('Error exporting absence CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await fetchAllAbsencesForExport();
      exportAbsenceToExcel(data, filters);
    } catch (error) {
      console.error('Error exporting absence Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = await fetchAllAbsencesForExport();
      exportAbsenceToPDF(data, filters);
    } catch (error) {
      console.error('Error exporting absence PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getAbsenceTypeColor = (absence: Pick<AbsenceRecord, 'absenceType' | 'reason'>) => {
    if (isManualDayOffAbsence(absence)) return 'success';
    switch (absence.absenceType) {
      case 'vacation': return 'success';
      case 'sick': return 'error';
      case 'personal': return 'info';
      case 'other': return 'default';
      case 'unexcused': return 'error';
      case 'holiday': return 'warning';
      case 'bereavement': return 'secondary';
      case 'maternity': return 'primary';
      case 'jury_duty': return 'info';
      case 'no_show': return 'error';
      case 'training': return 'success';
      case 'suspension': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string, employeeTimezone?: string) => {
    // DATEONLY fields are stored as 'YYYY-MM-DD' strings
    // Treat them as dates in the employee's timezone (where the absence was created)
    // This matches how other reports handle timezone conversion
    if (!dateString) return '';
    
    // Use employee's timezone if available, otherwise use viewer's timezone, fallback to UTC
    const timezone = employeeTimezone || user?.timezone || TimezoneService.getUserTimezone() || 'UTC';
    
    // If it's a DATEONLY string (YYYY-MM-DD), treat it as midnight in the employee's timezone
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Parse as date in employee's timezone (midnight)
      const dateInTimezone = moment.tz(dateString, 'YYYY-MM-DD', timezone);
      // Format for display
      return dateInTimezone.format('MMM D, YYYY');
    }
    
    // If it includes time, parse and format using employee's timezone
    try {
      // Try parsing as UTC first (if it's a full datetime)
      if (dateString.includes('T') || dateString.includes(' ')) {
        return moment.utc(dateString).tz(timezone).format('MMM D, YYYY');
      }
      // Otherwise treat as date string in employee's timezone
      return moment.tz(dateString, 'YYYY-MM-DD', timezone).format('MMM D, YYYY');
    } catch (e) {
      // Fallback to simple parsing
      return moment(dateString).format('MMM D, YYYY');
    }
  };

  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const toggleSort = (col: 'employee' | 'type' | 'start' | 'end' | 'created' | 'status') => {
    if (sortBy === col) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir(col === 'created' || col === 'start' || col === 'end' ? 'desc' : 'asc');
    }
  };

  const sortedAbsences = React.useMemo(() => {
    const list: AbsenceRecord[] = absenceData?.data ? [...absenceData.data] : [];
    list.sort((a, b) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'employee':
          av = `${a.user.lastName}${a.user.firstName}`.toLowerCase();
          bv = `${b.user.lastName}${b.user.firstName}`.toLowerCase();
          break;
        case 'type':
          av = a.absenceType; bv = b.absenceType; break;
        case 'start':
          av = new Date(a.startDate).getTime();
          bv = new Date(b.startDate).getTime();
          break;
        case 'end':
          av = new Date(a.endDate).getTime();
          bv = new Date(b.endDate).getTime();
          break;
        case 'status':
          av = a.status; bv = b.status; break;
        case 'created':
        default:
          av = new Date(a.createdAt).getTime();
          bv = new Date(b.createdAt).getTime();
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [absenceData?.data, sortBy, sortDir]);

  if (!['manager', 'administrator', 'super_user'].includes(user?.role || '')) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Absence Report
        </Typography>
        <Alert severity="warning">
          Access denied. Only managers, administrators, and super users can access absence reports.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Absence Report
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Box>

      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={usersData?.data?.data || []}
                  value={filters.userId ? usersData?.data?.data?.find((user: any) => user.id === filters.userId) || null : null}
                  onChange={(event, newValue) => {
                    handleFilterChange('userId', newValue?.id || '');
                  }}
                  getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName} (${option.employeeId})` : ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Employee" placeholder="Search employee..." />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.firstName} {option.lastName} ({option.employeeId})
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No employees found"
                  clearOnEscape
                  selectOnFocus
                  handleHomeEndKeys
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const searchLower = inputValue.toLowerCase();
                    return options.filter(option =>
                      `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                      (option.employeeId && option.employeeId.toLowerCase().includes(searchLower))
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Department"
                  value={filters.departmentId}
                  onChange={(value) => handleFilterChange('departmentId', String(value))}
                  options={(departmentsData?.data?.data || []).map((dept: any) => ({
                    value: dept.id,
                    label: dept.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Absence Type"
                  value={filters.absenceType}
                  onChange={(value) => handleFilterChange('absenceType', String(value))}
                  options={[
                    { value: 'vacation', label: 'Vacation' },
                    { value: 'sick', label: 'Sick Leave' },
                    { value: 'personal', label: 'Personal' },
                    { value: 'other', label: 'Other' },
                    { value: 'unexcused', label: 'Unexcused' },
                    { value: 'holiday', label: 'Holiday' },
                    { value: 'bereavement', label: 'Bereavement' },
                    { value: 'maternity', label: 'Maternity/Paternity' },
                    { value: 'jury_duty', label: 'Jury Duty' },
                    { value: 'no_show', label: 'No Show' },
                    { value: 'training', label: 'Training' },
                    { value: 'suspension', label: 'Suspension' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Status"
                  value={filters.status}
                  onChange={(value) => handleFilterChange('status', String(value))}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Records per page"
                  value={filters.limit}
                  onChange={(value) => handleFilterChange('limit', Number(value))}
                  options={[25, 50, 100].map((value) => ({
                    value,
                    label: String(value),
                  }))}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={() => refetch()}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleResetFilters}>
                Reset
              </Button>
              <Button
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                onClick={handleExportMenuOpen}
              >
                Export
              </Button>
              {canShareReports && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setShareDialogOpen(true)}
                  disabled={!absenceData?.data || absenceData.data.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Share
                </Button>
              )}
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportCSV}>
                  <ListItemIcon>
                    <CsvIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Export as CSV</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportExcel}>
                  <ListItemIcon>
                    <ExcelIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Export as Excel</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportPDF}>
                  <ListItemIcon>
                    <PdfIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Export as PDF</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load absence data. Please try again later.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Absence Records
              {absenceData?.pagination && (
                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                  ({absenceData.pagination.total} total records)
                </Typography>
              )}
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('employee')}>Employee {sortBy==='employee'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell>Employee ID</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('type')}>Absence Type {sortBy==='type'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('start')}>Start Date {sortBy==='start'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('end')}>End Date {sortBy==='end'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status {sortBy==='status'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Approved By</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('created')}>Created {sortBy==='created'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAbsences.map((absence: AbsenceRecord) => (
                      <TableRow key={absence.id}>
                        <TableCell>
                          {absence.user.firstName} {absence.user.lastName}
                        </TableCell>
                        <TableCell>{absence.user.employeeId}</TableCell>
                        <TableCell>
                          <Chip
                            label={getApprovedAbsenceDisplayLabel({
                              absenceType: absence.absenceType,
                              reason: absence.reason,
                            })}
                            color={getAbsenceTypeColor(absence) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(absence.startDate, absence.user?.timezone)}</TableCell>
                        <TableCell>{formatDate(absence.endDate, absence.user?.timezone)}</TableCell>
                        <TableCell>
                          {calculateDuration(absence.startDate, absence.endDate)} day(s)
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={absence.status.charAt(0).toUpperCase() + absence.status.slice(1)}
                            color={getStatusColor(absence.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {absence.reason || '-'}
                        </TableCell>
                        <TableCell>
                          {absence.approvedByUser 
                            ? `${absence.approvedByUser.firstName} ${absence.approvedByUser.lastName}`
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          {TimezoneService.formatDateTimeConsistent(absence.createdAt, user?.timezone || 'UTC')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {absenceData?.data?.length === 0 && (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No absence records found for the selected filters.
                  </Typography>
                </Box>
              )}

              {absenceData?.pagination && absenceData.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={absenceData.pagination.totalPages}
                    page={absenceData.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="absence"
          reportFilters={filters}
          reportTitle="Absence Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default AbsenceReportPage;
